import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Input,
  Select,
  HStack,
  VStack,
  Text,
  Spinner,
  useToast,
  InputGroup,
  InputLeftElement,
  Icon,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Code,
  Flex,
  Spacer,
} from '@chakra-ui/react';
import { FiSearch, FiFilter, FiEye, FiRefreshCw } from 'react-icons/fi';

const API_BASE = '/api/v1';

function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    action: '',
    model_type: '',
    username: '',
    from_date: '',
    to_date: '',
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
  });

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.action) params.append('action', filters.action);
      if (filters.model_type) params.append('model_type', filters.model_type);
      if (filters.username) params.append('username', filters.username);
      if (filters.from_date) params.append('from_date', filters.from_date);
      if (filters.to_date) params.append('to_date', filters.to_date);
      
      const url = `${API_BASE}/audit-logs/?${params.toString()}`;
      const response = await fetch(url, { headers: getAuthHeaders() });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(Array.isArray(data) ? data : data.results || []);
      } else {
        toast({ title: 'Error fetching audit logs', status: 'error', duration: 3000 });
      }
    } catch (e) {
      toast({ title: 'Error fetching audit logs', status: 'error', duration: 3000 });
    }
    setLoading(false);
  }, [filters, toast]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      action: '',
      model_type: '',
      username: '',
      from_date: '',
      to_date: '',
    });
  };

  const viewDetails = (log) => {
    setSelectedLog(log);
    onOpen();
  };

  const getActionBadge = (action) => {
    const colors = {
      create: 'green',
      update: 'blue',
      delete: 'red',
    };
    return <Badge colorScheme={colors[action] || 'gray'}>{action}</Badge>;
  };

  const getModelBadge = (modelType) => {
    const colors = {
      instance: 'purple',
      instance_limits: 'orange',
    };
    const labels = {
      instance: 'Instance',
      instance_limits: 'Limits',
    };
    return <Badge colorScheme={colors[modelType] || 'gray'}>{labels[modelType] || modelType}</Badge>;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <Box>
      <Flex align="center" mb={6}>
        <Heading size="lg">Audit Log</Heading>
        <Spacer />
        <Button leftIcon={<FiRefreshCw />} onClick={fetchLogs} isLoading={loading}>
          Refresh
        </Button>
      </Flex>

      {/* Filters */}
      <Box bg="white" p={4} borderRadius="lg" boxShadow="md" mb={6}>
        <HStack spacing={4} mb={4}>
          <Icon as={FiFilter} color="gray.500" />
          <Text fontWeight="medium" color="gray.600">Filters</Text>
        </HStack>
        
        <VStack spacing={4} align="stretch">
          <HStack spacing={4} flexWrap="wrap">
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <Icon as={FiSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Search by name..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </InputGroup>
            
            <Select
              placeholder="All Actions"
              value={filters.action}
              onChange={(e) => handleFilterChange('action', e.target.value)}
              maxW="150px"
            >
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
            </Select>
            
            <Select
              placeholder="All Types"
              value={filters.model_type}
              onChange={(e) => handleFilterChange('model_type', e.target.value)}
              maxW="180px"
            >
              <option value="instance">Instance</option>
              <option value="instance_limits">Instance Limits</option>
            </Select>
            
            <Input
              placeholder="Username"
              value={filters.username}
              onChange={(e) => handleFilterChange('username', e.target.value)}
              maxW="150px"
            />
          </HStack>
          
          <HStack spacing={4}>
            <HStack>
              <Text fontSize="sm" color="gray.500">From:</Text>
              <Input
                type="date"
                value={filters.from_date}
                onChange={(e) => handleFilterChange('from_date', e.target.value)}
                maxW="180px"
              />
            </HStack>
            
            <HStack>
              <Text fontSize="sm" color="gray.500">To:</Text>
              <Input
                type="date"
                value={filters.to_date}
                onChange={(e) => handleFilterChange('to_date', e.target.value)}
                maxW="180px"
              />
            </HStack>
            
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          </HStack>
        </VStack>
      </Box>

      {/* Table */}
      <Box bg="white" borderRadius="lg" boxShadow="md" overflow="hidden">
        {loading ? (
          <Flex justify="center" py={12}>
            <Spinner size="xl" color="purple.500" />
          </Flex>
        ) : logs.length === 0 ? (
          <Text textAlign="center" py={12} color="gray.500">
            No audit logs found
          </Text>
        ) : (
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Timestamp</Th>
                <Th>Action</Th>
                <Th>Type</Th>
                <Th>Object</Th>
                <Th>User</Th>
                <Th>IP Address</Th>
                <Th>Details</Th>
              </Tr>
            </Thead>
            <Tbody>
              {logs.map(log => (
                <Tr key={log.id} _hover={{ bg: 'gray.50' }}>
                  <Td fontSize="sm">{formatDate(log.created_at)}</Td>
                  <Td>{getActionBadge(log.action)}</Td>
                  <Td>{getModelBadge(log.model_type)}</Td>
                  <Td fontWeight="medium" maxW="200px" isTruncated>{log.object_name}</Td>
                  <Td>{log.username || '-'}</Td>
                  <Td fontSize="sm" fontFamily="mono">{log.ip_address || '-'}</Td>
                  <Td>
                    <Button
                      size="sm"
                      variant="ghost"
                      leftIcon={<FiEye />}
                      onClick={() => viewDetails(log)}
                    >
                      View
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* Details Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Audit Log Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedLog && (
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between">
                  <Text fontWeight="bold">Timestamp:</Text>
                  <Text>{formatDate(selectedLog.created_at)}</Text>
                </HStack>
                
                <HStack justify="space-between">
                  <Text fontWeight="bold">Action:</Text>
                  {getActionBadge(selectedLog.action)}
                </HStack>
                
                <HStack justify="space-between">
                  <Text fontWeight="bold">Type:</Text>
                  {getModelBadge(selectedLog.model_type)}
                </HStack>
                
                <HStack justify="space-between">
                  <Text fontWeight="bold">Object:</Text>
                  <Text>{selectedLog.object_name}</Text>
                </HStack>
                
                <HStack justify="space-between">
                  <Text fontWeight="bold">Object ID:</Text>
                  <Text>{selectedLog.object_id || '-'}</Text>
                </HStack>
                
                <HStack justify="space-between">
                  <Text fontWeight="bold">User:</Text>
                  <Text>{selectedLog.username || '-'}</Text>
                </HStack>
                
                <HStack justify="space-between">
                  <Text fontWeight="bold">IP Address:</Text>
                  <Text fontFamily="mono">{selectedLog.ip_address || '-'}</Text>
                </HStack>
                
                <Box>
                  <Text fontWeight="bold" mb={2}>Changes:</Text>
                  <Box
                    bg="gray.50"
                    p={4}
                    borderRadius="md"
                    maxH="300px"
                    overflow="auto"
                  >
                    <Code
                      display="block"
                      whiteSpace="pre-wrap"
                      fontSize="sm"
                      bg="transparent"
                    >
                      {JSON.stringify(selectedLog.changes, null, 2)}
                    </Code>
                  </Box>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default AuditLog;
